import { Component, Inject, OnDestroy, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';

import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { merge, startWith, Subscription, tap } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { AccountGroup, Constraint, ProjectionFilter } from '@vendasta/account-group';
import { Router } from '@angular/router';
import { FormatAddressToStringPipe } from '@vendasta/businesses';
import { MatInput } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatDivider } from '@angular/material/divider';

import { InViewportModule } from 'ng-in-viewport';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { BusinessSelectorModalService } from './business-selector-modal.service';
import { ContactSelectorModalService } from './contact-selector-modal.service';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { MatListItemMeta } from '@angular/material/list';

export interface DialogData {
  accountGroupID?: string;
  partnerId: string;
  marketId: string;
  projectionFilterOverride?: ProjectionFilter;
  hideCreateBusiness?: boolean;
}

export interface ContactData {
  contactID: string;
  accountGroupID: string;
  email: string;
  name: string;
}

@Component({
  selector: 'email-business-contact-selector-modal',
  imports: [
    CommonModule,
    MatButtonToggleGroup,
    MatIcon,
    MatButtonToggle,
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    GalaxyFormFieldModule,
    MatDialogTitle,
    MatDivider,
    MatDialogContent,
    FormatAddressToStringPipe,
    InViewportModule,
    MatButton,
    TranslateModule,
    GalaxyLoadingSpinnerModule,
    MatListItemMeta,
  ],
  templateUrl: './business-contact-selector-modal.component.html',
  styleUrl: './business-contact-selector-modal.component.scss',
})
export class BusinessContactSelectorModalComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription[] = [];
  protected readonly Array = Array;
  protected readonly businessSearchFormField = new UntypedFormControl('');
  protected readonly contactSearchFormField = new UntypedFormControl('');
  protected type: 'Contact' | 'Business' = 'Contact';

  protected contacts: Signal<ContactData[]>;
  protected contactLoading: Signal<boolean>;
  protected contactHasMore: Signal<boolean>;
  protected contactLoadingFirstPage: Signal<boolean>;

  protected businesses: Signal<AccountGroup[]>;
  protected businessLoading: Signal<boolean>;
  protected businessHasMore: Signal<boolean>;
  protected businessLoadingFirstPage: Signal<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) readonly data: DialogData,
    private readonly dialogRef: MatDialogRef<BusinessContactSelectorModalComponent>,
    private readonly router: Router,
    private readonly businessSelectorModalService: BusinessSelectorModalService,
    private readonly contactSelectorModalService: ContactSelectorModalService,
  ) {}

  ngOnInit(): void {
    this.contactSelectorModalService.fetchContacts();
    this.businessSelectorModalService.initializeBusinesses();

    this.businesses = this.businessSelectorModalService.business;
    this.businessHasMore = this.businessSelectorModalService.hasMore;
    this.businessLoading = this.businessSelectorModalService.loading;
    this.businessSelectorModalService.data = this.data;
    this.businessLoadingFirstPage = this.businessSelectorModalService.loadingFirstPage;

    this.contacts = this.contactSelectorModalService.contacts;
    this.contactHasMore = this.contactSelectorModalService.hasMore;
    this.contactLoading = this.contactSelectorModalService.loading;
    this.contactSelectorModalService.data = {
      accountGroupID: this.data.partnerId,
    };
    this.contactLoadingFirstPage = this.contactSelectorModalService.loadingFirstPage;

    this.subscriptions.push(
      merge(this.contactSearchFormField.valueChanges, this.businessSearchFormField.valueChanges)
        .pipe(
          startWith(''),
          tap(() => {
            if (this.type === 'Contact') {
              this.contactSelectorModalService.searchFormFieldValue = this.contactSearchFormField.value;
            } else {
              this.businessSelectorModalService.searchFormFieldValue = this.businessSearchFormField.value;
            }
          }),
        )
        .subscribe(),
    );
  }

  loadMoreAccounts(inViewportEvent: any): void {
    if (inViewportEvent.visible) {
      this.businessSelectorModalService.loadMore = true;
    }
  }

  loadMoreContacts(inViewportEvent: any): void {
    if (inViewportEvent.visible) {
      this.contactSelectorModalService.loadMore = true;
    }
  }

  selectBusiness(business: AccountGroup): void {
    this.dialogRef.close({
      business: business,
    });
  }

  selectContact(contact: ContactData): void {
    this.dialogRef.close({
      contact: contact,
    });
  }

  createBusiness(): void {
    this.dialogRef.close();
    this.router.navigateByUrl(`/business/search`);
  }

  getPartnerIdFromConstraints(constraints: Constraint[]): string {
    return constraints.find((element) => element?.foreignKey?.kind === 'PartnerAccountGroup')?.foreignKey?.key;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.reset();
  }

  reset(): void {
    this.contactSelectorModalService.resetState();
    this.businessSelectorModalService.resetState();

    this.contactSearchFormField.reset();
    this.businessSearchFormField.reset();
  }
}
